import { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDate, todayAthens } from "../../../lib/format";
import { errMsg } from "../../../lib/xutil";
import { useApi } from "../../customer/hooks";
import { Field, Panel, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { useConfirm } from "../../../components/common/ConfirmDialog";

const tone = { pending: "bg-amber-100 text-amber-800", approved: "bg-brand/10 text-brand", rejected: "bg-rose-50 text-rose-600", cancelled: "bg-slate-100 text-slate-500" };

export const LeaveList = ({ rows, team, onChange, testId }) => {
  const { t } = useI18n();
  const confirm = useConfirm();
  const decide = async (r, approve) => {
    const note = approve ? true : await confirm({ title: t("x.hr.leave.reject"), body: r.name, input: { label: t("x.hr.leave.rejectNote"), minLength: 2 }, danger: true, confirmLabel: t("x.hr.leave.reject") });
    if (!note) return;
    try { await api.post(`/hr/leave/${r.id}/decision`, { approve, note: approve ? null : note }); onChange(); } catch (e) { toast.error(errMsg(t, e)); }
  };
  const cancel = async (r) => { try { await api.post(`/hr/leave/${r.id}/cancel`); onChange(); } catch (e) { toast.error(errMsg(t, e)); } };
  if (!rows.length) return <p className="text-sm text-slate-500">{t("x.hr.leave.empty")}</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center gap-2 py-3 text-sm" data-testid={`leave-${r.id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-navy">{team && `${r.name} · `}{t(`x.hr.leave.types.${r.type}`)} · {fmtDate(r.date_from)}–{fmtDate(r.date_to)}</p>
            <p className="text-xs text-slate-500">{t("x.hr.leave.days", { n: r.days })}{r.note ? ` · ${r.note}` : ""}{r.decision_note ? ` · ${r.decision_note}` : ""}</p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tone[r.status]}`}>{t(`x.hr.leave.statuses.${r.status}`)}</span>
          {team && r.status === "pending" && <><button type="button" onClick={() => decide(r, true)} className={`${btnPrimary} h-9 px-4`} data-testid={`leave-approve-${r.id}`}>{t("x.hr.leave.approve")}</button>
            <button type="button" onClick={() => decide(r, false)} className={`${btnGhost} h-9 px-4 text-rose-600`} data-testid={`leave-reject-${r.id}`}>{t("x.hr.leave.reject")}</button></>}
          {!team && r.status === "pending" && <button type="button" onClick={() => cancel(r)} className={`${btnGhost} h-9 px-4`} data-testid={`leave-cancel-${r.id}`}>{t("x.hr.leave.cancel")}</button>}
        </li>
      ))}
    </ul>
  );
};

export const LeavePanel = ({ me, onChange }) => {
  const { t } = useI18n();
  const [f, setF] = useState({ type: "annual", date_from: todayAthens(7), date_to: todayAthens(7), note: "" });
  const mine = useApi("leaveMine", "/hr/leave", { scope: "mine" }, { staleTime: 0 });
  const b = me.balance;
  const submit = async () => {
    try { await api.post("/hr/leave", { ...f, note: f.note || null }); toast.success(t("x.hr.leave.sent")); mine.refetch(); onChange(); } catch (e) { toast.error(errMsg(t, e)); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
      <div className="space-y-4">
        <Panel title={t("x.hr.leave.balance", { y: b.year })} sub={t("x.hr.leave.seniority", { n: b.years_of_service })} testId="leave-balance">
          <dl className="grid grid-cols-4 gap-2 text-center">{["entitlement", "used", "pending", "remaining"].map((k) => (
            <div key={k} className={`rounded-xl p-2 ${k === "remaining" ? "bg-brand/10" : "bg-canvas"}`}><dt className="text-[11px] text-slate-500">{t(`x.hr.leave.${k}`)}</dt><dd className="text-xl font-bold text-navy" data-testid={`leave-${k}`}>{b[k]}</dd></div>))}</dl>
        </Panel>
        <Panel title={t("x.hr.leave.request")} testId="leave-form">
          <div className="mb-3 flex flex-wrap gap-2">{["annual", "sick", "unpaid", "other"].map((k) => <button key={k} type="button" className={chip(f.type === k)} onClick={() => setF((s) => ({ ...s, type: k }))} data-testid={`leave-type-${k}`}>{t(`x.hr.leave.types.${k}`)}</button>)}</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("x.from")}><DateField value={f.date_from} onChange={(v) => v && setF((s) => ({ ...s, date_from: v, date_to: s.date_to < v ? v : s.date_to }))} testId="leave-from" /></Field>
            <Field label={t("x.to")}><DateField value={f.date_to} onChange={(v) => v && setF((s) => ({ ...s, date_to: v }))} testId="leave-to" /></Field>
          </div>
          <input value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} placeholder={t("x.hr.leave.note")} className={`${input} mt-2`} data-testid="leave-note" />
          <button type="button" onClick={submit} className={`${btnPrimary} mt-3 w-full`} data-testid="leave-submit">{t("x.hr.leave.submit")}</button>
        </Panel>
      </div>
      <Panel title={t("x.hr.leave.mine")}>{mine.data && <LeaveList rows={mine.data} onChange={() => { mine.refetch(); onChange(); }} testId="leave-mine" />}</Panel>
    </div>
  );
};

export const TeamCalendar = ({ month }) => {
  const { t, lang } = useI18n();
  const r = useApi("hrCalendar", "/hr/calendar", { month }, { staleTime: 0 });
  if (!r.data) return null;
  const days = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const first = (new Date(`${month}-01T12:00:00Z`).getUTCDay() + 6) % 7;
  const hol = Object.fromEntries(r.data.holidays.map((h) => [h.date, h]));
  return (
    <div className="grid grid-cols-7 gap-1 text-xs" data-testid="team-calendar">
      {t("x.hr.days").map((d) => <div key={d} className="py-1 text-center font-medium text-slate-500">{d}</div>)}
      {Array.from({ length: first }).map((_, i) => <div key={`e${i}`} />)}
      {Array.from({ length: days }).map((_, i) => {
        const d = `${month}-${String(i + 1).padStart(2, "0")}`;
        const who = r.data.leaves.filter((l) => l.date_from <= d && l.date_to >= d);
        const wk = (first + i) % 7 > 4;
        return (
          <div key={d} className={`min-h-[64px] rounded-lg p-1 ${hol[d] ? "bg-brand/10" : wk ? "bg-slate-50" : "bg-white"} border border-slate-100`} data-testid={`cal-${d}`}>
            <p className="font-semibold text-navy">{i + 1}</p>
            {hol[d] && <p className="truncate text-[10px] text-brand" title={hol[d][`name_${lang}`]}>{hol[d][`name_${lang}`]}</p>}
            {who.map((l) => <p key={l.id} className={`truncate rounded px-1 text-[10px] ${l.status === "approved" ? "bg-navy text-white" : "bg-amber-100 text-amber-800"}`} title={l.name}>{l.name}</p>)}
          </div>
        );
      })}
    </div>
  );
};
